import { ClientApiError } from '../api/client';

export interface PhotoBatchDecision {
  /** Stop issuing further uploads in this batch. */
  stop: boolean;
  /**
   * Reload the parent referral because its state, or the caller's access to
   * it, may have changed underneath the batch.
   */
  reload: boolean;
  /** Count this file as a failed upload in the end-of-batch tally. */
  countFailed: boolean;
}

/**
 * Decides what a field-photo upload batch should do after one file's upload
 * throws.
 *
 * The photos endpoint (functions/api/referrals/[id]/photos.ts) never emits
 * the `referral_changed` code the notes/assign/recon/status endpoints use.
 * Instead, a referral going terminal (or the caller's access to it changing)
 * surfaces there as `canAddPhoto` failing, which is a 403. A referral that
 * has vanished is a 404. The per-referral photo cap is a 409. All three mean
 * the referral itself is stale and every remaining upload in the batch is
 * doomed the same way, so the batch stops and the parent reloads, whatever
 * the JSON error code string says.
 *
 * A network error (status 0) means the client has no connection. The next
 * upload will almost certainly fail identically, so the batch stops too, but
 * a reload is pointless while offline, so none is requested.
 *
 * Everything else, most notably 413 (too large) and 415 (wrong type), is a
 * problem with the one file, not the referral. The batch keeps going and the
 * file is counted as failed so the caller can report a total at the end.
 */
export function decidePhotoBatchOutcome(error: unknown): PhotoBatchDecision {
  if (error instanceof ClientApiError) {
    if (error.status === 0) {
      return { stop: true, reload: false, countFailed: false };
    }
    if (error.status === 403 || error.status === 404 || error.status === 409) {
      return { stop: true, reload: true, countFailed: false };
    }
    return { stop: false, reload: false, countFailed: true };
  }
  // Not an API error at all (e.g. downscaleImage throwing on a corrupt
  // image): treat it the same as a file-specific failure.
  return { stop: false, reload: false, countFailed: true };
}
